import * as Sentry from '@sentry/react';
import { AllowedReducer, Constants } from '@leverege/api-redux';
import I18N from '@leverege/i18n';
import Molten from '@leverege/molten';
import { Context } from '@leverege/plugin';
import { Toast } from '@leverege/ui-elements';
import { GlobalState } from '@leverege/ui-redux';
import { canSendTagRingCommand } from './Util.js';
import { amplitudeRingTagTrackEvent, getRingTagEventSource } from '../../tracking/AmplitudeRingTagTrackingEvent.js';
const {
  t,
  tIcon
} = I18N.ns('recovr.details.findAsset.FindAssetAction');
const ACTION = 'ringTag';
export default {
  id: 'recovr.findAsset.FindAssetAction',
  name: 'Find Asset',
  matches: [{
    client: ['DataCardDetail'],
    use: 'actionBar',
    objectType: 'assetTags'
  }],
  isPrepared: context => {
    const targets = Context.getTargetsOfType(context, 'assetTags');
    if (targets == null || targets.length === 0 || targets.length > 1) {
      return true;
    }
    const asset = GlobalState.get().models.lookup?.[targets[0].id]; // We have to pull the item from state, given that in the case of the selection, the item remains stale.
    const tag = asset.data.tag;
    if (tag == null) {
      return true;
    }
    const orgId = tag.data?.organization?.id;
    const companyId = tag.data?.company?.id;
    const locationId = tag.data?.location?.id;

    // It isn't an actual resourceId, but something closer to a ref.
    // Permissioned role will always be set at the company or location level.
    const resourceId = [orgId, companyId, locationId].filter(x => x != null).join('/');
    if (resourceId.length === 0) {
      return true;
    }
    const a = AllowedReducer.get(GlobalState.get(), resourceId);
    return a[ACTION] != null;
  },
  prepare: async context => {
    const targets = Context.getTargetsOfType(context, 'assetTags');
    const asset = GlobalState.get().models.lookup?.[targets[0].id]; // We have to pull the item from state, given that in the case of the selection, the item remains stale.
    const tag = asset.data.tag;
    const tagId = tag.id;
    const orgId = tag.data?.organization?.id;
    const companyId = tag.data?.company?.id;
    const locationId = tag.data?.location?.id;

    // It isn't an actual resourceId, but something closer to a ref.
    // Permissioned role will always be set at the company or location level.
    const resourceId = [orgId, companyId, locationId].filter(x => x != null).join('/');
    const a = AllowedReducer.get(GlobalState.get(), resourceId);
    if (a[ACTION] != null) {
      return a[ACTION];
    }
    return GlobalState.dispatch(async (dispatch, getState, {
      api
    }) => {
      let nApi;
      if (orgId && companyId && locationId) {
        nApi = api.interface(process.env.SYSTEM_ID, 'organization').obj(orgId).grp('companies').obj(companyId).grp('locations').obj(locationId).grp('tags').obj(`${tagId}`);
      } else if (companyId && locationId) {
        nApi = api.interface(process.env.SYSTEM_ID, 'company').obj(companyId).grp('locations').obj(locationId).grp('tags') // Has to be kept, interface service route only exists for the tag blueprint type.
        .obj(tagId);
      } else if (companyId) {
        nApi = api.interface(process.env.SYSTEM_ID, 'company').obj(companyId).grp('tags') // Has to be kept, interface service route only exists for the tag blueprint type.
        .obj(tagId);
      } else {
        nApi = api.interface(process.env.SYSTEM_ID, 'location').obj(locationId).grp('tags') // Has to be kept, interface service route only exists for the tag blueprint type.
        .obj(tagId);
      }
      const res = await nApi.srv('kudelskidevice').post(`device/${tagId}/command`, null, {
        test: true
      });
      dispatch({
        type: Constants.ALLOWED_SET,
        resourceId,
        action: ACTION,
        allowed: res.allowed === true
      });
      return res.allowed;
    });
  },
  handles: context => {
    const targets = Context.getTargetsOfType(context, 'assetTags');
    if (targets == null || targets.length === 0 || targets.length > 1) {
      return false;
    }
    const asset = GlobalState.get().models.lookup?.[targets[0].id]; // We have to pull the item from state, given that in the case of the selection, the item remains stale.
    const tag = asset.data.tag;
    if (tag == null) {
      return false;
    }
    const orgId = tag.data?.organization?.id;
    const companyId = tag.data?.company?.id;
    const locationId = tag.data?.location?.id;

    // It isn't an actual resourceId, but something closer to a ref.
    // Permissioned role will always be set at the company or location level.
    const resourceId = [orgId, companyId, locationId].filter(x => x != null).join('/');
    if (resourceId.length === 0) {
      return false;
    }
    const a = AllowedReducer.get(GlobalState.get(), resourceId);
    return a[ACTION] === true && canSendTagRingCommand(tag);
  },
  appearance: ({
    context
  }) => {
    const targets = Context.getTargetsOfType(context, 'assetTags');
    const asset = GlobalState.get().models.lookup?.[targets[0].id]; // We have to pull the item from state, given that in the case of the selection, the item remains stale.
    const tag = asset.data.tag;
    return {
      disabled: !canSendTagRingCommand(tag),
      name: t('name'),
      icon: tIcon('icon')
    };
  },
  perform: data => {
    const {
      context
    } = data;
    const onCommand = context.onCommand;
    const targets = Context.getTargetsOfType(context, 'assetTags');
    const asset = GlobalState.get().models.lookup?.[targets[0].id]; // We have to pull the item from state, given that in the case of the selection, the item remains stale.
    const tag = asset.data.tag;
    GlobalState.dispatch(async (dispatch, getState, {
      api
    }) => {
      try {
        const systemId = Molten.getConfig('api/systemId');
        const tagId = tag.id;
        const orgId = tag.data?.organization?.id;
        const companyId = tag.data?.company?.id;
        const locationId = tag.data?.location?.id;
        let nApi;
        if (orgId && companyId && locationId) {
          nApi = api.interface(systemId, 'organization').obj(orgId).grp('companies').obj(companyId).grp('locations').obj(locationId).grp('tags').obj(`${tagId}`);
        } else if (companyId && locationId) {
          nApi = api.interface(systemId, 'company').obj(companyId).grp('locations').obj(locationId).grp('tags').obj(`${tagId}`);
        } else if (companyId) {
          nApi = api.interface(systemId, 'company').obj(companyId).grp('tags').obj(tagId);
        } else {
          nApi = api.interface(systemId, 'location').obj(locationId).grp('tags').obj(tagId);
        }
        await nApi.srv('kudelskidevice').post(`device/${tagId}/command`, {
          command: {
            type: 'peripheralDevice.request.ring'
          }
        }, {});

        // Initiate tag device data subscription to correctly mirror the state of the tag
        // in the UI without waiting for periodic refreshes.
        onCommand();
        Toast.success(t('success'));

        /** AMPLITUDE TRACKING CODE */
        const {
          id
        } = data.action;
        const screen = data.context?.clientProps?.client;
        const TARGET_ID = 'recovr.findAsset.FindAssetAction';
        const isDetailsScreen = screen === 'ItemInfoScreen';
        const source = isDetailsScreen ? 'Find Asset - Details Screen' : 'Find Asset - List Card';

        /** AMPLITUDE TRACKING CODE */
        const eventSource = getRingTagEventSource(context.clientProps?.client);
        amplitudeRingTagTrackEvent(eventSource, 'ring');
        /** END OF AMPLITUDE TRACKING CODE */
      } catch (err) {
        Sentry.captureException(err);
        console.error(err);
        Toast.error([t('error'), err?.message]);
      }
    });
  }
};